import { createTheme, alpha, type Theme } from "@mui/material/styles";
// Mở rộng type của theme để khai báo được styleOverrides cho MuiDataGrid.
import "@mui/x-data-grid/themeAugmentation";
import { enUS, viVN, zhCN } from "@mui/x-data-grid/locales";
import type { Lang } from "./i18n";

/** Bảng màu khớp với biến CSS trong index.css để MUI hoà vào phần layout tự viết. */
const PALETTE = {
  dark: {
    bg: "#0b0e14",
    paper: "#141824",
    soft: "#1b2130",
    divider: "#242b3b",
    text: "#e7eaf3",
    muted: "#8b93a7",
    accent: "#3b82f6",
    accentStrong: "#2563eb",
    good: "#10b981",
    bad: "#ef4444",
    warn: "#f59e0b",
    // Đổ bóng mềm (Soft UI Evolution) cho card/paper trên nền tối
    shadowSm: "0 1px 2px rgb(0 0 0 / 0.28), 0 1px 1px rgb(0 0 0 / 0.2)",
    shadowMd: "0 10px 30px rgb(0 0 0 / 0.38)",
  },
  light: {
    bg: "#f6f8fb",
    paper: "#ffffff",
    soft: "#eef2f8",
    divider: "#e4e9f2",
    text: "#0f172a",
    muted: "#64748b",
    accent: "#2563eb",
    accentStrong: "#1d4ed8",
    good: "#059669",
    bad: "#dc2626",
    warn: "#d97706",
    shadowSm: "0 1px 2px rgb(15 23 42 / 0.06), 0 1px 3px rgb(15 23 42 / 0.04)",
    shadowMd: "0 12px 28px rgb(15 23 42 / 0.1)",
  },
} as const;

const GRID_LOCALES = { vi: viVN, en: enUS, zh: zhCN };

export function buildTheme(mode: "light" | "dark", lang: Lang): Theme {
  const c = PALETTE[mode];
  const accent = c.accent;

  return createTheme(
    {
      palette: {
        mode,
        primary: { main: accent, dark: c.accentStrong },
        success: { main: c.good },
        error: { main: c.bad },
        warning: { main: c.warn },
        background: { default: c.bg, paper: c.paper },
        divider: c.divider,
        text: { primary: c.text, secondary: c.muted },
        // Bề mặt "mềm" dùng cho hover / vùng chip nền — khớp --bg-soft cũ.
        action: {
          hover: alpha(c.muted, mode === "dark" ? 0.12 : 0.08),
          selected: alpha(accent, 0.16),
        },
      },
      shape: { borderRadius: 10 },
      typography: {
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        fontSize: 13,
        h1: { fontSize: 22, fontWeight: 700, lineHeight: 1.2 },
        h2: { fontSize: 15, fontWeight: 600, lineHeight: 1.3 },
        h3: { fontSize: 14, fontWeight: 600, lineHeight: 1.3 },
        button: { textTransform: "none", fontWeight: 600 },
        // Nhãn phụ đề dạng uppercase nhỏ (label của Stat / cột) đi qua caption/overline.
        overline: {
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          lineHeight: 1.4,
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            // Thanh cuộn mảnh, hoà theo nền — trước đây body dùng font-size 14.
            body: { fontSize: 14 },
            "*::-webkit-scrollbar": { width: 10, height: 10 },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(c.muted, 0.35),
              borderRadius: 8,
              border: `2px solid ${c.bg}`,
            },
            "*::-webkit-scrollbar-thumb:hover": { backgroundColor: alpha(c.muted, 0.55) },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: "none" },
            // Card/Stat (variant outlined) có chiều sâu mềm thay vì phẳng lì.
            outlined: {
              borderColor: c.divider,
              boxShadow: c.shadowSm,
            },
          },
        },
        MuiButton: {
          defaultProps: { size: "small", disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: 8,
              paddingInline: 14,
              fontWeight: 600,
              transition:
                "background-color .16s ease, border-color .16s ease, box-shadow .16s ease",
              "&.MuiButton-containedPrimary": {
                boxShadow: `0 1px 2px ${alpha(c.accentStrong, 0.4)}`,
                "&:hover": { backgroundColor: c.accentStrong },
              },
            },
          },
        },
        MuiIconButton: {
          defaultProps: { size: "small" },
        },
        MuiTextField: {
          defaultProps: { size: "small" },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: { borderRadius: 8, backgroundColor: mode === "dark" ? c.bg : c.paper },
          },
        },
        MuiChip: {
          defaultProps: { size: "small" },
          styleOverrides: { root: { fontWeight: 600 } },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              fontWeight: 600,
              border: "none",
              paddingBlock: 5,
              paddingInline: 12,
              color: c.muted,
              "&.Mui-selected": {
                color: accent,
                backgroundColor: c.paper,
                "&:hover": { backgroundColor: c.paper },
              },
            },
          },
        },
        MuiToggleButtonGroup: {
          styleOverrides: {
            root: { backgroundColor: alpha(c.muted, 0.14), padding: 3, borderRadius: 8, gap: 2 },
            grouped: { borderRadius: "6px !important", border: "none" },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: { textTransform: "none", fontWeight: 600, minHeight: 40, paddingInline: 14 },
          },
        },
        MuiTabs: {
          styleOverrides: { root: { minHeight: 40 } },
        },
        MuiDialog: {
          styleOverrides: { paper: { borderRadius: 12, backgroundImage: "none" } },
        },
        MuiTooltip: {
          defaultProps: { arrow: true },
          styleOverrides: {
            tooltip: { fontSize: 12, whiteSpace: "pre-line", maxWidth: 340 },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: {
              border: "none",
              // Cột co giãn theo container thay vì cố định — bảng tự thích ứng bề rộng.
              "--DataGrid-overlayHeight": "180px",
              // Số liệu canh cột thẳng hàng, không nhảy khi đổi chữ số.
              fontVariantNumeric: "tabular-nums",
            },
            // Header nền mờ nhẹ để tách khỏi vùng dữ liệu.
            columnHeaders: { borderColor: c.divider },
            columnHeader: {
              backgroundColor: mode === "dark" ? c.bg : c.soft,
            },
            columnHeaderTitle: {
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: c.muted,
            },
            row: {
              "&:hover": { backgroundColor: alpha(c.muted, mode === "dark" ? 0.08 : 0.06) },
              "&.Mui-selected": {
                backgroundColor: alpha(accent, 0.12),
                "&:hover": { backgroundColor: alpha(accent, 0.16) },
              },
            },
            cell: {
              borderColor: c.divider,
              "&:focus, &:focus-within": { outline: "none" },
            },
            footerContainer: { borderColor: c.divider, minHeight: 44 },
          },
        },
      },
    },
    GRID_LOCALES[lang],
  );
}
